import React, {useRef, useState} from 'react';
import { connect } from 'react-redux';
import { snackActions } from '../../sc-utilities/SnackBar';
import { withStyles, Tooltip, IconButton, Typography } from '@material-ui/core';
import { hasPermission, formatDate } from '../../sc-utilities';
import IntegrityCheckList from './IntegrityCheckList';
import { CloudDownload, CheckCircle } from '@material-ui/icons';
import {download} from '../../sc-utilities/download.utils';
import { CustomChip } from "../../sc-components";
import config from "../../../config/config";


const styles = theme => ({
    root: {
        width: `98.9%`,
        height: 'auto',
        paddingTop: theme.spacing(),
        paddingLeft: theme.spacing(),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%'
        }
    },
    chipInProgress: {
        ...theme.typography.button,
        color: theme.palette.info.dark,
        padding: 1,
        margin: 1,
    },
    chipResolved: {
        ...theme.typography.button,
        color: theme.palette.success.dark,
        padding: 1,
        margin: 1,
    },
    chipPending: {
        ...theme.typography.button,
        color: theme.palette.warning.dark,
        padding: 1,
        margin: 1,
    },
});


const IntegrityCheck = props => {
    const tableRef = useRef();
    const { classes, columns, translations, permissions, activeCustomer } = props;
    const [solveModalState, setSolveModalState] = useState({ open: false, IntegrityCheckID: null, Status: null, Type: null });

    let visible = columns && columns['IntegrityCheck'];
    let tableColumns = [
        {
            title: translations["action"] || "Action", field: 'IntegrityCheckID',
            hidden: visible ? visible['IntegrityCheckID'] : false,
            cellStyle: { minWidth: 100, maxWidth: 100 },
            filtering: false,
            render: rowData => <>
                <Tooltip title={translations["integrity_check_result"] || "Download integrity check result"}>
                    <IconButton aria-label={translations["integrity_check_result"] || "Download integrity check result"} value={rowData.IntegrityCheckID}
                        onClick={handleDownload}>
                        <CloudDownload />
                    </IconButton>
                </Tooltip>
                {rowData.Status === "pending" ?
                    <Tooltip title={translations["solve"] || "Solve "}>
                        <IconButton aria-label={translations["solve"] || "Solve "} value={rowData.IntegrityCheckID}
                            onClick={() => handleSolve(rowData.IntegrityCheckID, rowData.Status, rowData.Type)}>
                            <CheckCircle />
                        </IconButton>
                    </Tooltip> : null}
            </>
        },
        {
            title: translations["type"] || 'Type', field: 'Type',
            hidden: visible ? visible['Type'] : false,
            filtering: true,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Type ? formatType(rowData) : '-'}</Typography>;
            }
        },
        {
            title: translations["description"] || 'Description', field: 'Description',
            hidden: visible ? visible['Description'] : false,
            filtering: true,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Description ? rowData.Description : '-'}</Typography>;
            }
        },
        {
            title: translations["status"] || 'Status', field: 'Status',
            hidden: visible ? visible['Status'] : false,
            filtering: true,
            render: rowData => {
                return rowData.Status ? <CustomChip
                    customChipKey={rowData.IntegrityCheckID}
                    className={classes[`chip${statusCapitalized(rowData.Status)}`]}
                    value={rowData.Status.toUpperCase()} />
                    : <Typography variant={'caption'}>-</Typography>
            }
        },
        {
            title: translations["created_at"] || 'Created at', field: 'CreatedAt',
            hidden: visible ? visible['CreatedAt'] : false,
            filtering: false,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.CreatedAt ? formatDate(rowData.CreatedAt) : '-'}</Typography>;
            }
        }

    ];

    const statusCapitalized = status => {
        return status.charAt(0).toUpperCase() + status.slice(1);
    };

    const formatType = (integrityCheck) => {
        switch (integrityCheck.Type) {
            case 'missingReports':
                integrityCheck.Type = 'Missing reports';
                break;
            case 'missingUniqueIDs':
                integrityCheck.Type = 'Missing unique IDs';
                break;
            case 'diskUsage':
                integrityCheck.Type = 'Disk usage';
                break;
            case 'missingArticles':
                integrityCheck.Type = 'Missing articles';
                break;
            case 'duplicateInventoryIDs':
                integrityCheck.Type = 'Duplicate Securaze IDs';
                break;
            default:
                integrityCheck.Type = `${integrityCheck.Type}`;
        }

        return integrityCheck.Type
    }

    const handleDownload = async event => {
        const objectURL = `${config.backend_domain}:${config.backend_port}/integrity-checks/download?IntegrityCheckID=${event.currentTarget.value}&ActiveCustomerID=${activeCustomer}`;
        let exportDataSuccessful = await download(objectURL);
        tableRef.current && tableRef.current.onQueryChange();
        if (!exportDataSuccessful) {
            return snackActions.snackBarComponent(translations['download_error_msg'] || `Could not download file `, 'error');
        }
    };

    const handleSolve = async (id, status, type) => {
        if (!id) {
            setSolveModalState({ open: false, IntegrityCheckID: null, Status: null, Type: null });
            return;
        }
        setSolveModalState({ ...solveModalState, open: true, Type: type, IntegrityCheckID: id, Status: status, ActiveCustomerID: activeCustomer });
    };



    return (
        <div className={classes.root}>
            {hasPermission(permissions, 'integrity_check_list') ?
                <IntegrityCheckList
                    tableColumns={tableColumns}
                    handleSolve={handleSolve}
                    solveModalState={solveModalState}
                    tableRef={tableRef}
                />
                :
                null}

        </div>
    );
};

const mapStateToProps = state => {
    return {
        activeCustomer: state?.core?.login?.activeCustomer?.CustomerID ?? null,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
        permissions: state?.core?.login?.permissions || []
    };
};

export default connect(
    mapStateToProps,
    null
)(withStyles(styles)(IntegrityCheck));
